import {useState, useEffect} from 'react';

import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import {setBackgroundImageAC, setPostThumbnailFitTypeAC} from 'features/postGenerator/slice';
import {postThumbnailFitTypes} from 'features/postGenerator/typescript';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import {useDropzone} from 'react-dropzone';
import {setModalAC} from 'store/modal/modalSlice';
import {isObjectUrl} from 'utils/helpers/isObjectUrl';

import SelectPostThumbnailFitType from './SelectPostThumbnailFitType/SelectPostThumbnailFitType';
import './PostChangeBackground.scss';

const suggestedImageUrls = [
  'images/suggested-defaults/1.jpg',
  'images/suggested-defaults/2.jpg',
  'images/suggested-defaults/3.jpg',
  'images/suggested-defaults/4.jpg',
];

export default function PostChangeBackground() {
  const dispatch = useSetGlobalState();

  const backgroundImageUrl = useGlobalState((state) => state.postGenerator.backgroundImage);

  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [fileErrorMessage, setFileErrorMessage] = useState('');

  const [postThumbnailFitType, setPostThumbnailFitType] = useState<postThumbnailFitTypes>(postThumbnailFitTypes.cover);

  const handleDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    if (isObjectUrl(imagePreviewUrl)) URL.revokeObjectURL(imagePreviewUrl);
    if (fileErrorMessage) setFileErrorMessage('');

    const file = acceptedFiles[0];

    const objectUrl = URL.createObjectURL(file);

    setImagePreviewUrl(objectUrl);
  };

  const {getRootProps, getInputProps, open, isDragActive, fileRejections} = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    onDrop: handleDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
    },
    maxSize: 20971520, // 20MB
  });

  const handleCancel = () => {
    dispatch(setModalAC(null));
  };

  const handleSave = () => {
    if (!imagePreviewUrl) return;

    if (isObjectUrl(backgroundImageUrl)) URL.revokeObjectURL(backgroundImageUrl);

    dispatch(setBackgroundImageAC(imagePreviewUrl));
    dispatch(setPostThumbnailFitTypeAC(postThumbnailFitType));
    dispatch(setModalAC(null));
  };

  useEffect(() => {
    if (!fileRejections.length) return;

    const error = fileRejections[0].errors[0];

    if (error.code === 'file-too-large') setFileErrorMessage('File is larger than 20MB');
    else if (error.code === 'file-invalid-type') setFileErrorMessage('File type must be .jpg, .jpeg, .png, .gif');
    else setFileErrorMessage(error.message);
  }, [fileRejections]);

  return (
    <div {...getRootProps({className: 'post-change-background', role: 'dialog'})}>
      {isDragActive && <div className="post-change-background__dropzone-indicator">Drop image here to upload</div>}
      <input {...getInputProps()} />
      <div className="post-change-background__header">
        <h1 className="post-change-background__title">Change Background</h1>
      </div>
      <div className="post-change-background__content">
        {imagePreviewUrl ? (
          <div className="post-change-background__image-preview">
            <div className="post-change-background__preview-settings">
              <SelectPostThumbnailFitType value={postThumbnailFitType} onChange={setPostThumbnailFitType} />
              <button className="post-change-background__preview-upload-btn" onClick={open}>
                Choose image
              </button>
            </div>
            {fileErrorMessage && <p className="post-change-background__error-msg">{fileErrorMessage}</p>}
            <div className="post-change-background__previewed-image-wrapper">
              <div className="post-change-background__previewed-image-frame">
                <div
                  style={{
                    backgroundImage: `url(${imagePreviewUrl})`,
                    backgroundSize: postThumbnailFitType,
                    backgroundRepeat: 'no-repeat',
                  }}
                  className="post-change-background__previewed-image"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="post-change-background__upload">
            <button className="post-change-background__upload-btn" onClick={open}>
              <Icon icon="camera" />
              Choose Image
            </button>
            {fileErrorMessage && <p className="post-change-background__error-msg">{fileErrorMessage}</p>}
            <p className="post-change-background__upload-note">
              Recommended dimensions 1080px x 1080px.
              <br /> Maximum upload file size is 20MB.
            </p>
          </div>
        )}
        <div className="post-change-background__suggestions">
          <p className="post-change-background__suggestions-title">Or select one from our library</p>
          <div className="post-change-background__suggested-images-wrapper">
            {suggestedImageUrls.map((suggestedImageUrl) => (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                style={{backgroundImage: `url(${suggestedImageUrl})`, backgroundSize: 'cover'}}
                key={suggestedImageUrl}
                className="post-change-background__suggested-image"
                onClick={() => {
                  if (isObjectUrl(imagePreviewUrl)) URL.revokeObjectURL(imagePreviewUrl);
                  if (fileErrorMessage) setFileErrorMessage('');

                  setImagePreviewUrl(suggestedImageUrl);
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="post-change-background__actions">
        <Button variant="tertiary-outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="tertiary" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
}
